import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { WebService } from './web.service';

@Injectable({
  providedIn: 'root'
})
export class OutboundDataService {
  constructor(private web: WebService) { }

  private get actionByGUID(): string | null {
    return localStorage.getItem('GUID');
  }

  // ---------------------------------------------------------------------
  // List / detail
  // ---------------------------------------------------------------------

  getOutboundOrders(params?: { IncludeHistory?: boolean; WarehouseID?: number; OrderStatusID?: number; Search?: string }): Observable<any> {
    const httpParams = params
      ? Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([name, value]) => ({ name, value }))
      : undefined;

    return this.web.getWebCall('outbound', 'orders', { params: httpParams, skipLoader: true });
  }

  getOutboundOrderDetail(orderId: number): Observable<any> {
    return this.web.getWebCall('outbound', `orders/${orderId}`);
  }

  // ---------------------------------------------------------------------
  // Freight / instructions
  // ---------------------------------------------------------------------

  saveFreight(payload: {
    OrderID: number;
    TruckingRate?: string;
    TruckingQuote?: string;
    Trucker?: number;
    WarehouseInstructions?: string;
    BOLInstructions?: string;
  }): Observable<any> {
    return this.web.getWebCall('outbound', 'orders/freight', {
      body: { ...payload, ActionByGUID: this.actionByGUID }
    });
  }

  // ---------------------------------------------------------------------
  // Pickup date — used by BOTH Order Management and Outbound
  // ---------------------------------------------------------------------

  changePickupDate(orderId: number, pickupDate: string): Observable<any> {
    return this.web.getWebCall('outbound', 'orders/pickup-date', {
      body: { OrderID: orderId, PickupDate: pickupDate, ActionByGUID: this.actionByGUID }
    });
  }

  getCapacityStatus(startDate?: string, endDate?: string, skipLoader = false): Observable<any> {
    const params: { name: string, value: any }[] = [];
    if (startDate) params.push({ name: 'StartDate', value: startDate });
    if (endDate) params.push({ name: 'EndDate', value: endDate });

    return this.web.getWebCall('outbound', 'capacity', { params, skipLoader });
  }

  // ---------------------------------------------------------------------
  // Hold / release (5.2.27)
  // ---------------------------------------------------------------------

  placeOnHold(orderId: number, reason: string): Observable<any> {
    return this.web.getWebCall('outbound', 'orders/hold', {
      body: { OrderID: orderId, Reason: reason, ActionByGUID: this.actionByGUID }
    });
  }

  releaseHold(orderId: number): Observable<any> {
    return this.web.getWebCall('outbound', 'orders/release-hold', {
      body: { OrderID: orderId, ActionByGUID: this.actionByGUID }
    });
  }

  // ---------------------------------------------------------------------
  // Documents (5.2.16–5.2.20)
  // ---------------------------------------------------------------------

  // NEW — replaces the old generateDocument(). PDF rendering now happens
  // client-side (PdfService + html2pdf.js), so the service needs two calls
  // instead of one: fetch the data the template needs, then separately send
  // back the base64 PDF the browser already rendered.

  /** Step 1: pull order/warehouse/carrier header + consolidated line items for a template. */
  getDocumentData(orderId: number): Observable<any> {
    return this.web.getWebCall('outbound', `documents/data/${orderId}`);
  }

  /** Step 2: upload the already-rendered PDF (base64) and record it. */
  sendGeneratedDocument(orderId: number, documentType: string, base64: string, fileName: string): Observable<any> {
    return this.web.getWebCall('outbound', 'documents/generate', {
      body: {
        OrderID: orderId,
        DocumentType: documentType,
        Base64: base64,
        FileName: fileName,
        ActionByGUID: this.actionByGUID
      }
    });
  }

  sendDocument(orderId: number, documentType: string, recipients: string[], base64?: string): Observable<any> {
    return this.web.getWebCall('outbound', 'documents/send', {
      body: { OrderID: orderId, DocumentType: documentType, Base64: base64, ActionByGUID: this.actionByGUID }
    });
  }

  // ---------------------------------------------------------------------
  // Reconciliation (5.2.21, 5.2.22)
  // ---------------------------------------------------------------------

  getReconciliation(pickupDate?: string): Observable<any> {
    return this.web.getWebCall('outbound', 'reconciliation', pickupDate
      ? { params: [{ name: 'PickupDate', value: pickupDate }] }
      : undefined);
  }


  // ---------------------------------------------------------------------
  // Ship-to address (5.2.14) — multiple addresses exist per order from 5.1;
  // this lets Outbound pick which one applies to THIS shipment, distinct
  // from whichever is flagged IsPrimary (a customer default, not a
  // per-shipment choice).
  // ---------------------------------------------------------------------

  getShippingAddresses(orderId: number): Observable<any> {
    return this.web.getWebCall('outbound', `orders/${orderId}/shipping-addresses`);
  }

  setShipToAddress(orderId: number, shipToAddressId: number): Observable<any> {
    return this.web.getWebCall('outbound', 'orders/ship-to', {
      body: { OrderID: orderId, ShipToAddressID: shipToAddressId, ActionByGUID: this.actionByGUID }
    });
  }

  // ---------------------------------------------------------------------
  // Warehouse contacts (5.2.18)
  // ---------------------------------------------------------------------

  getWarehouseContacts(locationId: number): Observable<any> {
    return this.web.getWebCall('outbound', `warehouse-contacts/${locationId}`);
  }

  mergeWarehouseContact(payload: {
    WarehouseContactID?: number;
    LocationID: number;
    ContactName?: string;
    ContactEmail: string;
    IsPrimary?: boolean;
    Inactive?: boolean;
  }): Observable<any> {
    return this.web.getWebCall('outbound', 'warehouse-contacts/merge', { body: payload });
  }

  createWarehouseBatch(warehouseId: number, orderIds: number[]): Observable<any> {
    return this.web.getWebCall('outbound', 'batches', {
      body: { WarehouseID: warehouseId, OrderIDs: orderIds, ActionByGUID: this.actionByGUID }
    });
  }

  getWarehouseBatches(): Observable<any[]> {
    return this.web.getWebCall('outbound', 'batches');
  }

  getDocumentPreviewUrl(filePath: string): Observable<any> {
    return this.web.getWebCall('outbound', 'documents/preview-url', {
      params: [{ name: 'path', value: filePath }]
    });
  }

  checkMissingPrices(inventoryIds: number[]): Observable<any> {
    return this.web.getWebCall('outbound', 'orders/check-missing-prices', {
      body: { InventoryIDs: inventoryIds }
    });
  }

  setZeroPriceOverride(orderId: number, override: boolean): Observable<any> {
    return this.web.getWebCall('outbound', 'orders/zero-price-override', {
      body: { OrderID: orderId, Override: override, ActionByGUID: this.actionByGUID }
    });
  }


}
